import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SearchBox } from '@fluentui/react-components';
import { cn } from '@/lib/cn';
import { useDebouncedCallback } from '@/hooks/useDebounce';

/**
 * Global search in the header.
 *
 * Writes the term to the URL (`?q=`) instead of holding it in local state, so
 * results are shareable, survive a refresh and can be read by any feature page.
 * Typing is debounced; Enter navigates to the calendar immediately.
 */

export interface GlobalSearchProps {
  /** Where Enter takes the user when they are not already on a searchable view. */
  target?: string;
  placeholder?: string;
  className?: string;
}

export function GlobalSearch({
  target = '/calendar',
  placeholder = 'Buscar salas, reuniones u organizadores',
  className,
}: GlobalSearchProps): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValue] = useState<string>(() => searchParams.get('q') ?? '');

  // Keep the box in sync when the URL changes from elsewhere (back button…).
  useEffect(() => {
    setValue(searchParams.get('q') ?? '');
  }, [searchParams]);

  const commit = useDebouncedCallback((term: string) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (term) next.set('q', term);
        else next.delete('q');
        return next;
      },
      { replace: true },
    );
  }, 350);

  const handleChange = useCallback(
    (_event: unknown, data: { value: string }) => {
      setValue(data.value);
      commit(data.value.trim());
    },
    [commit],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const term = value.trim();
      navigate(term ? `${target}?q=${encodeURIComponent(term)}` : target);
    },
    [navigate, target, value],
  );

  return (
    <form role="search" onSubmit={handleSubmit} className={cn('min-w-0 flex-1', className)}>
      <SearchBox
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        aria-label="Buscar en salas y reservaciones"
        appearance="filled-lighter"
        className="w-full"
      />
    </form>
  );
}
